import { useState, useMemo } from 'react';
import { useDashboardStats } from './hooks/useDashboardStats';

const FEE_RATE = 0.05;

export default function AdminDashboardPage() {
  const {
    completedMatches,
    totalCompleted,
    totalAmount,
    totalFee,
    netAmount,
    monthlyStats,
    avgAmount,
    avgFee,
    loading,
    error,
    refetch,
  } = useDashboardStats();

  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const filteredMatches = useMemo(() => {
    if (!selectedMonth) return completedMatches;
    return completedMatches.filter((m) => m.completed_at?.startsWith(selectedMonth));
  }, [completedMatches, selectedMonth]);

  const filteredStats = useMemo(() => {
    const total = filteredMatches.reduce((s, m) => s + (m.total_amount || 0), 0);
    const fee = Math.round(total * FEE_RATE);
    return { total, fee, net: total - fee, count: filteredMatches.length };
  }, [filteredMatches]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f7faf9' }}>
      {/* Header */}
      <div className="w-full border-b border-gray-100 bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#2E7D6B' }}>
              <i className="ri-dashboard-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">코리아케어러스 관리자</p>
              <h1 className="text-sm font-bold text-gray-900">매칭 통계 &amp; 수수료 현황</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refetch}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>새로고침
            </button>
            <a
              href="/admin/match-list"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-hand-heart-line"></i>매칭내역
            </a>
            <a
              href="/admin/care-list"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-file-list-3-line"></i>신청 목록
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl animate-spin mb-3" style={{ color: '#2E7D6B' }}></i>
            <p className="text-sm text-gray-400">통계 데이터를 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <i className="ri-error-warning-line text-3xl text-red-300 mb-3"></i>
            <p className="text-sm font-semibold text-red-400 mb-1">데이터 로딩 실패</p>
            <p className="text-xs text-gray-400 mb-4">{error}</p>
            <button
              onClick={refetch}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              다시 시도
            </button>
          </div>
        ) : (
          <>
            {/* 핵심 지표 카드 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: '완료된 매칭', value: `${totalCompleted}건`, icon: 'ri-check-double-line', color: '#059669', bg: '#d1fae5' },
                { label: '총 간병비', value: `${(totalAmount / 10000).toLocaleString()}만원`, icon: 'ri-exchange-funds-line', color: '#2563eb', bg: '#dbeafe' },
                { label: '중개 수수료', value: `${(totalFee / 10000).toLocaleString()}만원`, icon: 'ri-bank-line', color: '#7c3aed', bg: '#ede9fe' },
                { label: '건당 평균 수수료', value: `${(avgFee / 10000).toFixed(1)}만원`, icon: 'ri-bar-chart-2-line', color: '#d97706', bg: '#fef3c7' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: s.bg }}>
                    <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">{s.label}</p>
                    <p className="text-lg font-bold text-gray-900">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 수수료 상세 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-funds-line text-[#7c3aed] text-lg" />
                  월별 매칭 &amp; 수수료 현황
                </h2>
                {monthlyStats.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-inbox-line text-gray-300 text-xl" />
                    </div>
                    <p className="text-sm text-gray-400">아직 완료된 매칭이 없습니다</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3">월</th>
                          <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3 text-right">매칭 건수</th>
                          <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3 text-right">총 간병비</th>
                          <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3 text-right">수수료</th>
                          <th className="text-xs font-semibold text-gray-400 py-2.5 text-right">순수익</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyStats.map((m) => (
                          <tr
                            key={m.month}
                            onClick={() => setSelectedMonth(selectedMonth === m.month ? null : m.month)}
                            className={`border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50/50 ${
                              selectedMonth === m.month ? 'bg-purple-50/30' : ''
                            }`}
                          >
                            <td className="py-3 pr-3">
                              <span className="text-sm font-bold text-gray-900">
                                {m.month.slice(0, 4)}년 {m.month.slice(5, 7)}월
                              </span>
                            </td>
                            <td className="py-3 pr-3 text-right">
                              <span className="text-sm font-semibold text-gray-700">{m.count}건</span>
                            </td>
                            <td className="py-3 pr-3 text-right">
                              <span className="text-sm text-gray-700">{(m.totalAmount / 10000).toFixed(0)}만원</span>
                            </td>
                            <td className="py-3 pr-3 text-right">
                              <span className="text-sm font-semibold text-[#7c3aed]">{(m.totalFee / 10000).toFixed(0)}만원</span>
                            </td>
                            <td className="py-3 text-right">
                              <span className="text-sm text-gray-600">{((m.totalAmount - m.totalFee) / 10000).toFixed(0)}만원</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200">
                          <td className="py-3 pr-3">
                            <span className="text-sm font-extrabold text-gray-900">합계</span>
                          </td>
                          <td className="py-3 pr-3 text-right">
                            <span className="text-sm font-extrabold text-gray-900">{totalCompleted}건</span>
                          </td>
                          <td className="py-3 pr-3 text-right">
                            <span className="text-sm font-extrabold text-gray-900">{(totalAmount / 10000).toFixed(0)}만원</span>
                          </td>
                          <td className="py-3 pr-3 text-right">
                            <span className="text-sm font-extrabold text-[#7c3aed]">{(totalFee / 10000).toFixed(0)}만원</span>
                          </td>
                          <td className="py-3 text-right">
                            <span className="text-sm font-extrabold text-gray-700">{(netAmount / 10000).toFixed(0)}만원</span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* 수수료 요약 */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col">
                <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-pie-chart-line text-[#F57C00] text-lg" />
                  수수료 요약
                </h2>
                <div className="flex-1 space-y-4">
                  <div className="bg-purple-50 rounded-xl p-4">
                    <p className="text-[11px] text-[#7c3aed] font-bold mb-1">총 중개 수수료</p>
                    <p className="text-2xl font-extrabold text-[#7c3aed]">{(totalFee / 10000).toFixed(0)}만원</p>
                    <p className="text-[10px] text-[#7c3aed]/60 mt-1">수수료율 {(FEE_RATE * 100).toFixed(0)}% 기준</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-[11px] text-[#059669] font-bold mb-1">간병인 총 정산액</p>
                    <p className="text-2xl font-extrabold text-[#059669]">{(netAmount / 10000).toFixed(0)}만원</p>
                    <p className="text-[10px] text-[#059669]/60 mt-1">간병인에게 지급된 총액</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <p className="text-[11px] text-amber-700 font-bold mb-1">건당 평균</p>
                    <div className="flex items-baseline justify-between">
                      <p className="text-lg font-extrabold text-amber-700">{(avgAmount / 10000).toFixed(0)}만원</p>
                      <p className="text-xs text-amber-600">수수료 {(avgFee / 10000).toFixed(1)}만원</p>
                    </div>
                    <p className="text-[10px] text-amber-700/60 mt-1">매칭 1건당 평균</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 최근 완료 매칭 목록 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
                  <i className="ri-history-line text-[#2E7D6B] text-lg" />
                  {selectedMonth
                    ? `${selectedMonth.slice(0, 4)}년 ${selectedMonth.slice(5, 7)}월 완료 매칭`
                    : '최근 완료 매칭'}
                  <span className="text-xs font-medium text-gray-400">({filteredMatches.length}건)</span>
                </h2>
                {selectedMonth && (
                  <button
                    onClick={() => setSelectedMonth(null)}
                    className="text-[11px] text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-close-line" />
                    필터 해제
                  </button>
                )}
              </div>

              {filteredMatches.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-inbox-line text-gray-300 text-xl" />
                  </div>
                  <p className="text-sm text-gray-400">
                    {selectedMonth ? '해당 월에 완료된 매칭이 없습니다' : '아직 완료된 매칭이 없습니다'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3">매칭번호</th>
                        <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3">간병 기간</th>
                        <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3 text-right">간병비</th>
                        <th className="text-xs font-semibold text-gray-400 py-2.5 pr-3 text-right">수수료</th>
                        <th className="text-xs font-semibold text-gray-400 py-2.5 text-right">정산액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMatches.slice(0, 20).map((m) => {
                        const fee = Math.round((m.total_amount || 0) * FEE_RATE);
                        const net = (m.total_amount || 0) - fee;
                        return (
                          <tr key={m.id} className="border-b border-gray-50">
                            <td className="py-2.5 pr-3">
                              <span className="text-xs font-semibold text-gray-700">
                                {m.match_number || '-'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3">
                              <span className="text-xs text-gray-500">
                                {m.start_date || '-'} ~ {m.end_date || '-'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-right">
                              <span className="text-xs text-gray-700">{(m.total_amount || 0).toLocaleString()}원</span>
                            </td>
                            <td className="py-2.5 pr-3 text-right">
                              <span className="text-xs font-semibold text-[#7c3aed]">{fee.toLocaleString()}원</span>
                            </td>
                            <td className="py-2.5 text-right">
                              <span className="text-xs font-semibold text-[#059669]">{net.toLocaleString()}원</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredMatches.length > 20 && (
                    <p className="text-center text-[11px] text-gray-400 mt-3">
                      최근 20건만 표시됩니다 (전체 {filteredMatches.length}건)
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}